import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { ScrollArea } from '@/components/ui/scroll-area.js';
import { Loader2, Search, Globe, Plus, ExternalLink } from 'lucide-react';
import { useContactStore } from '@/stores/contactStore.js';
import { discoverServices, serviceDescriptorToAgentCard, type ServiceDescriptor } from '@/services/registryService.js';

interface DiscoverServicesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function DiscoverServicesDialog({ open, onOpenChange }: DiscoverServicesDialogProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [services, setServices] = useState<ServiceDescriptor[]>([]);
    const [total, setTotal] = useState(0);
    const [addingService, setAddingService] = useState<string | null>(null);

    const addContact = useContactStore((state) => state.addContact);
    const getContactByName = useContactStore((state) => state.getContactByName);

    useEffect(() => {
        if (open) {
            loadServices();
        }
    }, [open]);

    const loadServices = async (query = '') => {
        setLoading(true);
        setError('');

        try {
            const result = await discoverServices({
                query: query || undefined,
                discoveryType: 'a2a',
                limit: 50,
            });

            setServices(result.services);
            setTotal(result.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load services');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        loadServices(searchQuery);
    };

    const handleAddService = async (service: ServiceDescriptor) => {
        setAddingService(service.id);
        setError('');

        try {
            // Check if already exists
            const existing = getContactByName(service.name);
            if (existing) {
                setError(`${service.name} is already in your contacts`);
                return;
            }

            // Convert to AgentCard and add
            const agentCard = serviceDescriptorToAgentCard(service);
            addContact(agentCard, {
                tags: ['network', 'discovered'],
            });

            // Success feedback
            setTimeout(() => {
                setAddingService(null);
            }, 1000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add contact');
        } finally {
            setTimeout(() => {
                setAddingService(null);
            }, 1000);
        }
    };

    const isServiceAdded = (serviceName: string) => {
        return !!getContactByName(serviceName);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] h-[600px] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5" />
                        Discover Agents
                    </DialogTitle>
                    <DialogDescription>
                        Browse and add other users' personal assistants from the Dexto Network. Each agent is unique to its owner.
                    </DialogDescription>
                </DialogHeader>

                {/* Search */}
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search agents by name, description, or tags..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            className="pl-9"
                            disabled={loading}
                        />
                    </div>
                    <Button onClick={handleSearch} disabled={loading}>
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Search className="h-4 w-4" />
                        )}
                    </Button>
                </div>

                {/* Results count */}
                {!loading && services.length > 0 && (
                    <div className="text-sm text-muted-foreground">
                        Found {total} agent{total !== 1 ? 's' : ''}
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                        {error}
                    </div>
                )}

                {/* Services List */}
                <ScrollArea className="flex-1 -mx-6 px-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : services.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <Globe className="h-12 w-12 text-muted-foreground/50 mb-3" />
                            <p className="text-sm text-muted-foreground">
                                {searchQuery ? 'No agents match your search' : 'No agents available'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3 pb-4">
                            {services.map((service) => {
                                const isAdded = isServiceAdded(service.name);
                                const isAdding = addingService === service.id;

                                return (
                                    <div
                                        key={service.id}
                                        className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-semibold truncate">
                                                        {service.name}
                                                    </h3>
                                                    <Badge variant="outline" className="text-xs">
                                                        {service.discoveryType.toUpperCase()}
                                                    </Badge>
                                                </div>

                                                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                                                    {service.description}
                                                </p>

                                                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                                                    <span>by {service.owner.username}</span>
                                                    {service.capabilities.streaming && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            Streaming
                                                        </Badge>
                                                    )}
                                                </div>

                                                {service.capabilities.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1">
                                                        {service.capabilities.tags.slice(0, 5).map((tag) => (
                                                            <Badge
                                                                key={tag}
                                                                variant="secondary"
                                                                className="text-xs"
                                                            >
                                                                {tag}
                                                            </Badge>
                                                        ))}
                                                        {service.capabilities.tags.length > 5 && (
                                                            <Badge variant="secondary" className="text-xs">
                                                                +{service.capabilities.tags.length - 5}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-2 shrink-0">
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleAddService(service)}
                                                    disabled={isAdded || isAdding}
                                                    className="gap-2"
                                                >
                                                    {isAdding ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : isAdded ? (
                                                        'Added'
                                                    ) : (
                                                        <>
                                                            <Plus className="h-4 w-4" />
                                                            Add
                                                        </>
                                                    )}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => window.open(service.url, '_blank')}
                                                    className="gap-2"
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </ScrollArea>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
